import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/config/supabase';
import type { CompleteOnboardingWizardOutput } from '@/types/onboarding';

/**
 * Onboarding Success Page
 *
 * This is the final screen of the onboarding wizard, shown after the user
 * completes or skips Step 4 (maternal grandparents).
 *
 * Backend State:
 * - User reaches this page when `completion_ready_at IS NOT NULL`
 * - This means Step 4 has been saved/skipped
 * - `completed_at` is still NULL at this point
 *
 * User Actions:
 * - Click "Create My Family Tree" button
 * - This calls `complete_onboarding_wizard()` RPC function
 * - RPC creates the tree and sets `completed_at = NOW()`
 * - User is redirected to dashboard
 *
 * Design:
 * - Follows Stitch HTML reference for visual design
 * - Maintains consistent header/layout from Steps 1-4
 * - Celebration/success theme with icon and messaging
 * - Shows summary of collected data (7 members across 3 generations)
 * - Single blue CTA button (no skip or back buttons)
 * - Loading state during tree creation
 * - Error handling with retry capability
 *
 * Route Guards:
 * - If user refreshes: stays on success page (state is `ready_to_finalize`)
 * - If user logs out/in: redirects to success page (state is `ready_to_finalize`)
 * - If user tries dashboard: redirects to success page (until CTA is clicked)
 * - If already completed: redirects to dashboard (state is `fully_completed`)
 *
 * @see E:\Job\IT\50Studios\Fiels\Clickup_Projects\projects\family-tree\files\stitch\stitch\wizard_success_refined\code.html
 */
export function OnboardingSuccessPage() {
  const { t } = useTranslation('onboarding');
  const { user, refreshProfile } = useAuth();
  const [isCreating, setIsCreating] = useState(false);

  /**
   * Handle Create Tree Button Click
   *
   * Calls the complete_onboarding_wizard RPC function to:
   * 1. Create the family tree
   * 2. Add all persons from onboarding session
   * 3. Create relationships between persons
   * 4. Mark onboarding as complete
   * 5. Return tree metadata
   */
  const handleCreateTree = async () => {
    if (!user || isCreating) return;

    setIsCreating(true);

    try {
      console.log('[handleCreateTree] Starting RPC call with user_id:', user.id);

      // Call RPC function to complete onboarding
      // Type assertion needed due to Supabase client type limitations with RPC functions
      const { data, error } = await supabase.rpc('complete_onboarding_wizard', {
        p_user_id: user.id,
      } as any);

      console.log('[handleCreateTree] RPC response received');
      console.log('[handleCreateTree] Raw data:', data);
      console.log('[handleCreateTree] Raw error:', error);
      console.log('[handleCreateTree] Full RPC response:', JSON.stringify({ data, error }, null, 2));

      if (error) {
        console.error('[handleCreateTree] RPC error:', error);
        throw error;
      }

      // Parse result (RPC returns array with single object)
      const result = (data as unknown as CompleteOnboardingWizardOutput[])[0];
      console.log('[handleCreateTree] Parsed result:', result);
      console.log('[handleCreateTree] Result keys:', result ? Object.keys(result) : 'null');

      if (!result) {
        console.error('[handleCreateTree] No result returned from RPC');
        throw new Error('No result returned from complete_onboarding_wizard');
      }

      console.log('[handleCreateTree] Extracted values:', {
        success: result.success,
        errorMessage: result.error_message,
        treeId: result.tree_id,
        personCount: result.person_count,
        relationshipCount: result.relationship_count
      });

      if (!result.success) {
        const backendError = result.error_message || 'Failed to create family tree';
        console.error('[handleCreateTree] Backend returned failure:', backendError);
        throw new Error(backendError);
      }

      console.log('[handleCreateTree] Tree created successfully!', {
        treeId: result.tree_id,
        personCount: result.person_count,
        relationshipCount: result.relationship_count
      });

      // Success! Show success message
      toast.success(
        t(
          'success.treeCreated',
          'Your family tree has been created successfully!'
        )
      );

      // CRITICAL: Refresh user profile to update onboarding_completed_at in frontend state
      // After this completes, the RequireOnboarding guard will detect the updated profile
      // and automatically redirect to dashboard
      console.log('[handleCreateTree] Refreshing user profile to get updated onboarding status...');
      try {
        await refreshProfile();
        console.log('[handleCreateTree] Profile refreshed successfully');
        console.log('[handleCreateTree] Guards will now handle automatic redirect to dashboard');
      } catch (refreshError) {
        console.error('[handleCreateTree] CRITICAL: Failed to refresh profile:', refreshError);
        // If profile refresh fails, we cannot proceed
        // Re-throw to prevent navigation and show error
        throw new Error('Failed to refresh user profile. Please try again.');
      }

      // Let the guard system handle the redirect automatically
      // The RequireOnboarding guard will detect the updated onboarding_completed_at
      // and redirect to dashboard without causing loops
    } catch (err) {
      console.error('[handleCreateTree] Failed to create family tree:', err);

      // Show error message
      const errorMessage =
        err instanceof Error
          ? err.message
          : t('success.errors.createFailed', 'Failed to create your family tree. Please try again.');

      toast.error(errorMessage);

      // Re-enable button to allow retry
      setIsCreating(false);
    }
  };

  return (
    <main className="flex-1 flex justify-center py-6 sm:py-8 md:py-12 px-4 sm:px-6">
      <div className="layout-content-container flex flex-col max-w-[640px] flex-1 items-center text-center">
        {/* Success Icon with Glow Effect */}
        <div className="relative mb-6 sm:mb-7 md:mb-8">
          {/* Glow background */}
          <div className="absolute inset-0 bg-primary/10 blur-2xl sm:blur-3xl rounded-full scale-150" />

          {/* Main icon container */}
          <div className="relative flex items-center justify-center size-24 sm:size-28 md:size-32 lg:size-40 rounded-full bg-gradient-to-br from-primary to-blue-600 text-white shadow-lg sm:shadow-xl shadow-primary/20">
            <span className="material-symbols-outlined text-4xl sm:text-5xl md:text-6xl lg:text-7xl">
              celebration
            </span>
          </div>

          {/* Check mark badge */}
          <div className="absolute -top-1.5 -right-1.5 sm:-top-2 sm:-right-2 bg-green-500 text-white p-1.5 sm:p-2 rounded-full border-2 sm:border-4 border-background dark:border-[#121520] shadow-lg">
            <span className="material-symbols-outlined text-base sm:text-lg md:text-xl font-bold">
              check
            </span>
          </div>
        </div>

        {/* Success Message */}
        <div className="space-y-2 sm:space-y-2.5 md:space-y-3 mb-6 sm:mb-8 md:mb-10 px-2">
          <h1 className="text-slate-900 dark:text-slate-100 tracking-tight text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold leading-tight">
            {t(
              'success.title',
              'Your family tree foundation is ready!'
            )}
          </h1>
          <p className="text-slate-600 dark:text-slate-400 text-sm sm:text-base md:text-lg font-normal max-w-md mx-auto">
            {t(
              'success.description',
              'Great job! We\'ve successfully mapped out three generations of your heritage for you to explore.'
            )}
          </p>
        </div>

        {/* Summary Card */}
        <div className="w-full mb-8 sm:mb-10 md:mb-12">
          <div className="flex items-center gap-3 sm:gap-4 md:gap-6 rounded-lg sm:rounded-xl bg-surface dark:bg-slate-800/50 p-4 sm:p-5 md:p-6 border border-primary/10 shadow-sm transition-all hover:shadow-md">
            {/* Icon */}
            <div className="flex-shrink-0 size-14 sm:size-16 md:size-20 rounded-md sm:rounded-lg bg-primary/5 flex items-center justify-center overflow-hidden">
              <div className="w-full h-full bg-slate-200 dark:bg-slate-700 flex items-center justify-center">
                <span className="material-symbols-outlined text-primary text-xl sm:text-2xl md:text-3xl opacity-50">
                  family_history
                </span>
              </div>
            </div>

            {/* Content */}
            <div className="flex flex-col text-left rtl:text-right flex-1 min-w-0">
              <h3 className="text-slate-900 dark:text-slate-100 text-base sm:text-lg md:text-xl font-bold leading-tight">
                {t('success.membersIdentified', '7 members identified')}
              </h3>
              <p className="text-slate-500 dark:text-slate-400 text-xs sm:text-sm font-medium mt-0.5 sm:mt-1">
                {t(
                  'success.generationsVerified',
                  'Across 3 generations • Verified connections'
                )}
              </p>

              {/* Avatar stack */}
              <div className="mt-2 sm:mt-3 flex items-center gap-1.5 sm:gap-2">
                <span className="flex -space-x-1.5 sm:-space-x-2">
                  <div className="size-5 sm:size-6 rounded-full border-2 border-surface dark:border-slate-800 bg-slate-300" />
                  <div className="size-5 sm:size-6 rounded-full border-2 border-surface dark:border-slate-800 bg-slate-400" />
                  <div className="size-5 sm:size-6 rounded-full border-2 border-surface dark:border-slate-800 bg-slate-500" />
                </span>
                <span className="text-[10px] sm:text-xs text-slate-400 font-medium">
                  {t('success.additionalMembers', '+4 others')}
                </span>
              </div>
            </div>

            {/* Chevron - hidden on mobile */}
            <div className="ltr:ml-auto rtl:mr-auto hidden sm:block">
              <button
                className="p-1.5 sm:p-2 text-primary hover:bg-primary/5 rounded-lg transition-colors"
                aria-label={t('success.viewDetails', 'View details')}
              >
                <span className="material-symbols-outlined text-lg sm:text-xl">chevron_right</span>
              </button>
            </div>
          </div>
        </div>

        {/* CTA Button */}
        <div className="flex flex-col gap-3 sm:gap-4 w-full sm:w-80 px-2">
          <Button
            onClick={handleCreateTree}
            disabled={isCreating}
            size="lg"
            className="w-full h-11 sm:h-12 md:h-14 px-6 sm:px-8 text-sm sm:text-base font-bold shadow-lg shadow-primary/25 hover:brightness-110 active:scale-[0.98] transition-all"
          >
            {isCreating ? (
              <span className="flex items-center justify-center gap-2">
                <span className="w-4 h-4 sm:w-5 sm:h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                <span className="hidden sm:inline">{t('success.creating', 'Creating your family tree...')}</span>
                <span className="sm:hidden">{t('success.creatingShort', 'Creating...')}</span>
              </span>
            ) : (
              <>
                <span className="hidden sm:inline">{t('success.cta', 'Create My Family Tree')}</span>
                <span className="sm:hidden">{t('success.ctaShort', 'Create Tree')}</span>
              </>
            )}
          </Button>
        </div>

        {/* Privacy Notice */}
        <div className="mt-10 sm:mt-12 md:mt-16 pt-6 sm:pt-7 md:pt-8 border-t border-primary/10 w-full">
          <div className="flex items-center justify-center gap-1.5 sm:gap-2 text-slate-400 text-xs sm:text-sm">
            <span className="material-symbols-outlined text-sm sm:text-base">
              shield_with_heart
            </span>
            <span>{t('success.privacy', 'Your data is encrypted and private')}</span>
          </div>
        </div>
      </div>
    </main>
  );
}
